import { submitExpense } from './api';
import { AuthExpiredError } from './auth';
import { type PendingExpense, db } from './db';
import { ApiError, NetworkError } from './http';

export interface EnqueueExpenseInput {
  tripId: string;
  descricao: string;
  valor: string;
  fotoDataUrl?: string;
}

/** Guarda o gasto localmente e o enfileira pro outbox — funciona 100% offline. */
export async function enqueueExpense(input: EnqueueExpenseInput): Promise<void> {
  const idempotencyKey = crypto.randomUUID();
  const record: PendingExpense = {
    tripId: input.tripId,
    descricao: input.descricao,
    valor: input.valor,
    idempotencyKey,
    status: 'pending',
    createdAt: Date.now(),
    attempts: 0,
    ...(input.fotoDataUrl ? { fotoDataUrl: input.fotoDataUrl } : {}),
  };
  await db.pendingExpenses.add(record);
}

let syncing = false;
type SyncListener = () => void;
const syncListeners = new Set<SyncListener>();

function setSyncing(value: boolean): void {
  syncing = value;
  for (const listener of syncListeners) listener();
}

export function isSyncing(): boolean {
  return syncing;
}

/** Assina mudanças no estado "sincronizando…" — reflete tanto gatilhos manuais quanto automáticos. */
export function subscribeSyncing(listener: SyncListener): () => void {
  syncListeners.add(listener);
  return () => {
    syncListeners.delete(listener);
  };
}

type SyncOutcome = 'synced' | 'failed' | 'retry' | 'stopped';

/** Erro que exige login de novo — encerra o loop, a UI redireciona pro login. */
function isAuthFailure(error: unknown): boolean {
  return error instanceof AuthExpiredError || (error instanceof ApiError && error.status === 401);
}

async function handleSyncError(
  item: PendingExpense & { localId: number },
  error: unknown,
): Promise<SyncOutcome> {
  if (isAuthFailure(error)) {
    await db.pendingExpenses.update(item.localId, {
      status: 'pending',
      attempts: item.attempts + 1,
      lastError: 'Sessão expirada — faça login novamente pra continuar sincronizando',
    });
    return 'stopped';
  }
  if (error instanceof ApiError && error.isClientError) {
    // 4xx "definitivo" (ex.: viagem ainda não aprovada) — não adianta re-tentar
    // sozinho, precisa de revisão manual (RNF-05, resolução de conflito).
    await db.pendingExpenses.update(item.localId, {
      status: 'failed',
      attempts: item.attempts + 1,
      lastError: error.message,
    });
    return 'failed';
  }
  // Falha de rede (ou erro inesperado) — mantém pendente pra próxima tentativa.
  await db.pendingExpenses.update(item.localId, {
    status: 'pending',
    attempts: item.attempts + 1,
    lastError: error instanceof Error ? error.message : 'Falha desconhecida',
  });
  return error instanceof NetworkError ? 'stopped' : 'retry';
}

async function syncOne(item: PendingExpense & { localId: number }): Promise<SyncOutcome> {
  try {
    const trip = await submitExpense(item.tripId, {
      descricao: item.descricao,
      valor: item.valor,
      idempotencyKey: item.idempotencyKey,
      ...(item.fotoDataUrl ? { fotoUrl: item.fotoDataUrl } : {}),
    });
    await db.pendingExpenses.update(item.localId, { status: 'synced', syncedAt: Date.now() });
    await db.trips.put({ ...trip, cachedAt: Date.now() });
    return 'synced';
  } catch (error) {
    return handleSyncError(item, error);
  }
}

/**
 * Processa a fila de sincronização (outbox), em ordem de criação — para no
 * primeiro item que exige sessão nova ou que falhou por falta de rede (as
 * próximas tentativas da fila também falhariam pelo mesmo motivo).
 */
export async function syncPendingExpenses(): Promise<{ synced: number; failed: number }> {
  if (syncing) return { synced: 0, failed: 0 };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { synced: 0, failed: 0 };
  }

  setSyncing(true);
  let synced = 0;
  let failed = 0;
  try {
    const queue = await db.pendingExpenses
      .where('status')
      .anyOf('pending', 'failed')
      .sortBy('createdAt');

    for (const item of queue) {
      const { localId } = item;
      if (localId === undefined) continue;
      await db.pendingExpenses.update(localId, { status: 'syncing' });

      const outcome = await syncOne({ ...item, localId });
      if (outcome === 'synced') synced += 1;
      else failed += 1;
      if (outcome === 'stopped') break;
    }
  } finally {
    setSyncing(false);
  }
  return { synced, failed };
}

/** Tenta reenviar um item marcado como `failed` (ação manual do usuário). */
export async function retryPendingExpense(localId: number): Promise<void> {
  await db.pendingExpenses.update(localId, { status: 'pending' });
  await syncPendingExpenses();
}

export async function discardPendingExpense(localId: number): Promise<void> {
  await db.pendingExpenses.delete(localId);
}

/**
 * Dispara a sincronização quando a conexão volta (evento `online`) — cobre o
 * caso central de RF-VIA-04 (viagem no interior sem sinal, sincroniza ao voltar).
 * Background Sync (SW) fica de fora por ora: só Chromium implementa a API, e o
 * gatilho por evento `online` já cobre o fluxo fim-a-fim de forma confiável.
 */
export function registerSyncTriggers(): () => void {
  const handleOnline = (): void => {
    void syncPendingExpenses();
  };
  window.addEventListener('online', handleOnline);
  if (navigator.onLine) {
    void syncPendingExpenses();
  }
  return () => window.removeEventListener('online', handleOnline);
}
